import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, DatePicker, Descriptions, Input, Select, Space, Table, Tooltip, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { ActivityScopeSelect } from "../../components/ActivityScopeSelect";
import { QtyInput } from "../../components/QtyInput";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { useApi } from "../../helpers/useApi";
import { fmtItem } from "../../helpers/format";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSitesQuery, useSiteStockQuery } from "../../helpers/queries";
import { DT_FORMATS } from "../../helpers/constants";
import { activityPath, bomItemActivityPath, bomItemsInScope, flattenActivities } from "../../helpers/activities";
import type { BOMActivity, BOMItem, Consumption, Paginated, ProjectBOMVersion } from "../../api-models";

const ACT_MESSAGES: Record<string, string> = {
  submit: "Submitted for approval.",
  cancel: "Consumption cancelled.",
  reverse: "Consumption reversed.",
};

interface DraftLine {
  key: string;
  bomItemId?: number;
  qty?: number;
  remarks?: string;
}

// Recording consumption is a draft (spec §15/§30.1), so a phone entry made in the field can
// be corrected before it touches the ledger. It reaches the ledger through approval, not
// through a post button: the crew submits, the site's approver signs off, and the sign-off
// posts it. A posted record is undone by an explicit reversal, never by editing.
//
// A record names the work first and the materials second, exactly like a request: the crew
// picks BOM activities and then the materials approved under them. That is what makes
// posted consumption roll up into per-activity project progress (the Activity Progress
// report) instead of a pile of item totals.
export default function ConsumptionDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();

  const isNew = id === "new";
  // Recorded from a project page: the project is already known, so only its sites are offered.
  const fromProjectId = Number(searchParams.get("projectId")) || undefined;
  const [siteId, setSiteId] = useState<number>();
  const [consumedOn, setConsumedOn] = useState<Dayjs>(dayjs());
  // Not editable on this page, but carried through a save so an update never blanks it.
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [record, setRecord] = useState<Consumption | null>(null);
  const [bomItems, setBomItems] = useState<BOMItem[]>([]);
  const [activityTree, setActivityTree] = useState<BOMActivity[]>([]);
  const [activityIds, setActivityIds] = useState<number[]>([]);

  const activities = useMemo(() => flattenActivities(activityTree), [activityTree]);
  const bomItemById = useMemo(() => new Map(bomItems.map((l) => [l.id, l])), [bomItems]);

  // Only MAPPED rows can be drawn against (BOMItem.Requestable in bom.go).
  function applyVersion(detail: ProjectBOMVersion) {
    setBomItems((detail.items ?? []).filter((l) => l.mapping === "MAPPED"));
    setActivityTree(detail.activities ?? []);
  }

  async function loadActiveVersion(projectId: number) {
    const versions = await request<Paginated<ProjectBOMVersion>>(
      { url: "/bom-versions", params: { projectId, status: "APPROVED_ACTIVE", pageSize: 1 } },
      { silent: true },
    );
    if (versions.data.length === 0) {
      setBomItems([]);
      setActivityTree([]);
      return;
    }
    applyVersion(await request<ProjectBOMVersion>({ url: `/bom-versions/${versions.data[0].id}` }, { silent: true }));
  }

  async function load() {
    if (isNew) return;
    const res = await request<Consumption>({ url: `/consumptions/${id}` });
    setRecord(res);
    setSiteId(res.siteId);
    setConsumedOn(dayjs(res.consumedOn));
    setNotes(res.notes ?? "");
    setActivityIds((res.activities ?? []).map((a) => a.activityId));
    setLines(
      (res.items ?? []).map((it) => ({
        key: String(it.id),
        bomItemId: it.bomItemId ?? undefined,
        qty: it.qty,
        remarks: it.remarks,
      })),
    );
    if (res.bomVersionId) {
      // The record stays pinned to the version it was booked against (spec §6.3), so its
      // activities and materials come from that version, not whatever is active now.
      applyVersion(await request<ProjectBOMVersion>({ url: `/bom-versions/${res.bomVersionId}` }, { silent: true }));
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const selectableSites = fromProjectId ? sites.filter((s) => s.projectId === fromProjectId) : sites;

  // A lone selectable site is picked by SiteSelect itself; the BOM version below then
  // loads off it without the user touching the picker.
  useEffect(() => {
    if (isNew && siteId) {
      const s = sites.find((x) => x.id === siteId);
      if (s) loadActiveVersion(s.projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, siteId, sites.length]);

  const canWrite = hasPermission(user, "Consumption.Write", isNew ? siteId : record?.siteId);
  const editable = isNew || record?.status === "DRAFT";

  // Posting draws the site's own store down and may not push it below what's reserved
  // there, so each line shows issuable qty (on hand − reserved) — that is the material the
  // crew can actually book against this site today.
  const { data: siteStock } = useSiteStockQuery(isNew ? siteId : record?.siteId);
  const availableByItem = useMemo(
    () => (siteStock ? new Map(siteStock.map((b) => [b.itemId, b.onHandQty - b.reservedQty])) : undefined),
    [siteStock],
  );
  function availableFor(bomItemId?: number): number | undefined {
    const itemId = bomItemId ? bomItemById.get(bomItemId)?.itemId : undefined;
    return itemId == null ? undefined : availableByItem?.get(itemId);
  }

  const scopedBomItems = useMemo(() => bomItemsInScope(bomItems, activityIds, activities), [bomItems, activityIds, activities]);

  // Grouped by activity path: that is the only way to tell two rows for the same material
  // apart when a BOM repeats it across activities (spec §7/§9.1 assumption 2).
  const groupedBomItemOptions = useMemo(() => {
    const groups = new Map<string, { value: number; label: string }[]>();
    for (const l of scopedBomItems) {
      const path = bomItemActivityPath(l, activities);
      const opts = groups.get(path) ?? [];
      opts.push({ value: l.id, label: l.item ? fmtItem(l.item) : String(l.itemId) });
      groups.set(path, opts);
    }
    return [...groups.entries()].map(([label, options]) => ({ label, options }));
  }, [scopedBomItems, activities]);

  function addLine() {
    setLines((prev) => [...prev, { key: `new-${Date.now()}-${Math.random()}` }]);
  }
  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  // Narrowing the work narrows the record: a line whose material is no longer in scope
  // would be refused at save, so it is dropped here with a note rather than left to fail.
  function changeActivities(ids: number[]) {
    setActivityIds(ids);
    const stillInScope = new Set(bomItemsInScope(bomItems, ids, activities).map((l) => l.id));
    setLines((prev) => {
      const kept = prev.filter((l) => l.bomItemId == null || stillInScope.has(l.bomItemId));
      if (kept.length < prev.length) {
        message.info(`${prev.length - kept.length} line(s) removed — their activity is no longer selected.`);
      }
      return kept;
    });
  }

  // One line per material approved under the selected work, pre-filled with what the site
  // actually holds — "we finished this activity, book what we used" in a single click.
  // Quantity is still the crew's to correct; the site balance is only the sensible start.
  function addAllFromScope() {
    const used = new Set(lines.map((l) => l.bomItemId));
    const stamp = Date.now();
    const additions = scopedBomItems
      .filter((l) => !used.has(l.id))
      .map((l, i) => ({ key: `new-${stamp}-${i}`, bomItemId: l.id, qty: availableFor(l.id) || undefined }));
    if (additions.length === 0) {
      message.info("Every item in the selected activities is already on this record.");
      return;
    }
    setLines((prev) => [...prev, ...additions]);
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/consumptions/${id}/${action}` });
    message.success(ACT_MESSAGES[action] ?? "Done.");
    load();
  }

  async function save() {
    if (!siteId) {
      message.error("Select a site.");
      return;
    }
    if (activityIds.length === 0) {
      message.error("Select the activities this material was used on.");
      return;
    }
    const items = lines
      .filter((l) => l.bomItemId && l.qty)
      .map((l) => ({ bomItemId: l.bomItemId, qty: l.qty, remarks: l.remarks ?? "" }));
    if (items.length === 0) {
      message.error("Add at least one item.");
      return;
    }
    const payload = { siteId, notes, consumedOn: consumedOn.toISOString(), activityIds, items };
    if (isNew) {
      const created = await request<Consumption>({ method: "POST", url: "/consumptions", data: payload });
      message.success("Consumption recorded.");
      navigate(`/consumption/${created.id}`, { replace: true });
      return;
    }
    await request({ method: "PUT", url: `/consumptions/${id}`, data: payload });
    message.success("Consumption updated.");
    load();
  }

  if (!isNew && !record) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "Record Consumption" : record!.docNo}
        subtitle={
          record ? (
            <>
              <SiteLabel site={record.site} project={record.project} /> — {dayjs(record.consumedOn).format(DT_FORMATS.date)}
            </>
          ) : undefined
        }
        extra={
          <Space wrap>
            {record && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={record.status} />
              </span>
            )}
            {editable && canWrite && (
              <Button type={isNew ? "primary" : "default"} onClick={save} loading={loading} data-tour="consumption-save">
                Save
              </Button>
            )}
            {record?.status === "DRAFT" && canWrite && (
              <Button type="primary" onClick={() => act("submit")} loading={loading} data-tour="consumption-submit">
                Submit for approval
              </Button>
            )}
            {(record?.status === "DRAFT" || record?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            {record?.status === "POSTED" && canWrite && (
              <Button danger onClick={() => act("reverse")} loading={loading}>
                Reverse
              </Button>
            )}
          </Space>
        }
      />
      {record?.status === "DRAFT" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Not submitted yet"
          description="Nothing has left site stock. Submit this record for approval — the approver's sign-off is what draws the material down and counts it as project usage."
        />
      )}
      {record?.status === "PENDING_APPROVAL" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Awaiting approval"
          description="Nothing has left site stock yet. It posts to the ledger the moment this is approved. Cancel it to take it back off the approver's desk."
        />
      )}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Site">
            {/* The site is fixed once recorded — it decides the store the posting draws down. */}
            {isNew ? (
              <SiteSelect
                data-tour="consumption-site"
                placeholder="Site"
                style={{ minWidth: 220 }}
                options={selectableSites.map((s) => ({ value: s.id, label: s.name }))}
                value={siteId}
                onChange={(v) => {
                  setSiteId(v);
                  setActivityIds([]);
                  setLines([]);
                }}
              />
            ) : (
              <SiteLabel site={record!.site} project={record!.project} />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            {editable && canWrite ? <DatePicker value={consumedOn} onChange={(v) => v && setConsumedOn(v)} /> : dayjs(record!.consumedOn).format(DT_FORMATS.date)}
          </Descriptions.Item>
          <Descriptions.Item label="Activities" span={2}>
            <ActivityScopeSelect
              data-tour="consumption-activities"
              activities={activityTree}
              value={activityIds}
              onChange={changeActivities}
              readOnly={!(editable && canWrite)}
              disabled={activityTree.length === 0}
              placeholder={activityTree.length === 0 ? "Select a site with an approved BOM first" : undefined}
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>
      {editable && canWrite ? (
        <Card size="small" title="Items used" data-tour="consumption-lines">
          {activityIds.length === 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Select the activities this material was used on"
              description="Consumption is booked against a piece of work. Choosing an activity offers the materials approved under it — and everything under its sub-activities — so the project's progress can be read per activity."
            />
          )}
          <Table<DraftLine>
            rowKey="key"
            dataSource={lines}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            columns={[
              {
                // Its own column so the code stays scannable down the page while the
                // material itself is picked from the grouped selector.
                title: "Code",
                key: "itemCode",
                width: 120,
                render: (_, row) => (row.bomItemId ? bomItemById.get(row.bomItemId)?.item?.code ?? "-" : "-"),
              },
              {
                title: "BOM Item",
                width: 320,
                render: (_, row) => {
                  const used = new Set(lines.filter((l) => l.key !== row.key).map((l) => l.bomItemId));
                  return (
                    <Select
                      showSearch
                      style={{ width: "100%" }}
                      placeholder={activityIds.length === 0 ? "Select an activity first" : "Select a BOM item"}
                      disabled={activityIds.length === 0}
                      value={row.bomItemId}
                      options={groupedBomItemOptions.map((g) => ({
                        label: g.label,
                        options: g.options.filter((o) => !used.has(o.value)),
                      }))}
                      filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                      onChange={(v) => updateLine(row.key, { bomItemId: v })}
                    />
                  );
                },
              },
              {
                title: "Qty",
                width: 200,
                render: (_, row) => {
                  const bl = row.bomItemId ? bomItemById.get(row.bomItemId) : undefined;
                  return <QtyInput value={row.qty} unit={bl?.item?.unit?.abbreviation} onChange={(v) => updateLine(row.key, { qty: v ?? undefined })} />;
                },
              },
              {
                // Not a cap: booking more than the site shows is a real correction case,
                // and posting is where the ledger refuses an overdraw. This is the crew's
                // sanity check while typing.
                title: "At this site",
                width: 130,
                render: (_, row) => {
                  const available = availableFor(row.bomItemId);
                  const bl = row.bomItemId ? bomItemById.get(row.bomItemId) : undefined;
                  if (available == null) return <Typography.Text type="secondary">—</Typography.Text>;
                  return (
                    <Typography.Text type={available > 0 ? undefined : "danger"}>
                      {available} {bl?.item?.unit?.abbreviation ?? ""}
                    </Typography.Text>
                  );
                },
              },
              {
                title: "Remarks",
                render: (_, row) => (
                  <Input value={row.remarks ?? ""} onChange={(e) => updateLine(row.key, { remarks: e.target.value })} />
                ),
              },
              {
                title: "",
                key: "remove",
                width: 48,
                render: (_, row) => <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removeLine(row.key)} />,
              },
            ]}
          />
          <Space style={{ marginTop: 8 }} wrap>
            <Button icon={<PlusOutlined />} onClick={addLine} disabled={activityIds.length === 0} data-tour="consumption-add-line">
              Add item
            </Button>
            <Tooltip title={activityIds.length === 0 ? "Select an activity first." : "One line per material approved under the selected activities, pre-filled with what this site holds."}>
              <Button icon={<PlusOutlined />} onClick={addAllFromScope} disabled={scopedBomItems.length === 0}>
                Add all items from selected activities
              </Button>
            </Tooltip>
          </Space>
        </Card>
      ) : (
        <Table
          rowKey="id"
          dataSource={record!.items ?? []}
          pagination={false}
          columns={[
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            {
              title: "Activity",
              render: (_, r) => activityPath(r.bomItemId ? bomItemById.get(r.bomItemId)?.activityId : undefined, activities),
            },
            { title: "Qty", dataIndex: "qty" },
            { title: "Remarks", dataIndex: "remarks" },
          ]}
        />
      )}
      {!isNew && <ApprovalTimeline docType="CONSUMPTION" docId={Number(id)} refreshKey={record!.status} onActed={load} />}
    </div>
  );
}
